/**
 * Session-scoped visitor journey (first-party, sessionStorage only).
 *
 * Records where the visitor came from (referrer, UTM, click ids) and the
 * ordered list of pages seen during the current tab session, so lead forms
 * can send that context with the request. Query strings are dropped from
 * page paths because they can carry prefilled contact data.
 */

export const VISITOR_JOURNEY_STORAGE_KEY = 'kendrick_journey_v1'
export const VISITOR_JOURNEY_MAX_PAGES = 30
const MAX_PATH_LENGTH = 200
const MAX_UTM_LENGTH = 120

export type TrafficChannel =
  | 'ai_assistant'
  | 'direct'
  | 'email'
  | 'organic_search'
  | 'organic_social'
  | 'paid_search'
  | 'paid_social'
  | 'referral'

export interface VisitorJourneyUtm {
  campaign?: string
  content?: string
  medium?: string
  source?: string
  term?: string
}

export interface VisitorJourneyPage {
  at: number
  path: string
}

export interface VisitorJourneyConsent {
  analytics?: boolean
  marketing?: boolean
  status: 'accepted' | 'partial' | 'refused' | 'pending' | 'unknown'
  /** CMPKit visitor id, to find the consent proof in CMPKit. */
  visitorId?: string
  /** When the visitor made the stored CMP choice (ms epoch). */
  decidedAt?: number
}

export interface VisitorJourney {
  clickIds?: string[]
  consent?: VisitorJourneyConsent
  id?: string
  landingPath: string
  pages: VisitorJourneyPage[]
  referrer?: string
  startedAt: number
  utm?: VisitorJourneyUtm
}

export interface VisitorJourneySnapshot extends VisitorJourney {
  channel: TrafficChannel
  durationSeconds: number
  pageCount: number
  referrerHost?: string
  /** Social network behind the visit, even without UTM (referrer, UTM or in-app browser). */
  socialNetwork?: SocialNetwork
}

export type SocialNetwork =
  | 'facebook'
  | 'instagram'
  | 'linkedin'
  | 'pinterest'
  | 'reddit'
  | 'snapchat'
  | 'threads'
  | 'tiktok'
  | 'whatsapp'
  | 'x'
  | 'youtube'

export const SOCIAL_NETWORK_LABELS: Record<SocialNetwork, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  pinterest: 'Pinterest',
  reddit: 'Reddit',
  snapchat: 'Snapchat',
  threads: 'Threads',
  tiktok: 'TikTok',
  whatsapp: 'WhatsApp',
  x: 'X',
  youtube: 'YouTube'
}

const NETWORK_BY_HOST: Array<[RegExp, SocialNetwork]> = [
  [/(^|\.)instagram\.com$/, 'instagram'],
  [/(^|\.)(pinterest\.[a-z.]+|pin\.it)$/, 'pinterest'],
  [/(^|\.)tiktok\.com$/, 'tiktok'],
  [/(^|\.)(facebook\.com|fb\.com|fb\.me|messenger\.com)$/, 'facebook'],
  [/(^|\.)(linkedin\.com|lnkd\.in)$/, 'linkedin'],
  [/(^|\.)(x\.com|twitter\.com|t\.co)$/, 'x'],
  [/(^|\.)(youtube\.com|youtu\.be)$/, 'youtube'],
  [/(^|\.)snapchat\.com$/, 'snapchat'],
  [/(^|\.)threads\.(net|com)$/, 'threads'],
  [/(^|\.)reddit\.com$/, 'reddit'],
  [/(^|\.)(whatsapp\.com|wa\.me)$/, 'whatsapp']
]

// In-app browsers identify themselves in the user agent, which catches
// visits whose referrer the app strips (they would otherwise look Direct).
const NETWORK_BY_USER_AGENT: Array<[RegExp, SocialNetwork]> = [
  [/Instagram/i, 'instagram'],
  [/Pinterest/i, 'pinterest'],
  [/musical_ly|BytedanceWebview|TikTok/i, 'tiktok'],
  [/FBAN|FBAV|FB_IAB|FBIOS/, 'facebook'],
  [/LinkedInApp/i, 'linkedin'],
  [/Snapchat/i, 'snapchat'],
  [/Barcelona/, 'threads'],
  [/Twitter/i, 'x']
]

const NETWORK_BY_UTM: Array<[RegExp, SocialNetwork]> = [
  [/^(instagram|ig|insta)$/, 'instagram'],
  [/^(pinterest|pin)$/, 'pinterest'],
  [/^(tiktok|tt)$/, 'tiktok'],
  [/^(facebook|fb|meta)$/, 'facebook'],
  [/^linkedin$/, 'linkedin'],
  [/^(x|twitter)$/, 'x'],
  [/^(youtube|yt)$/, 'youtube'],
  [/^snapchat$/, 'snapchat'],
  [/^threads$/, 'threads']
]

export function detectSocialNetwork(input: {
  referrer?: string
  utm?: VisitorJourneyUtm
  userAgent?: string | null
}): SocialNetwork | undefined {
  const source = input.utm?.source?.toLowerCase().trim() || ''
  const fromUtm = NETWORK_BY_UTM.find(([pattern]) => pattern.test(source))?.[1]
  if (fromUtm) return fromUtm

  const host = getReferrerHost(input.referrer) || ''
  const fromHost = NETWORK_BY_HOST.find(([pattern]) => pattern.test(host))?.[1]
  if (fromHost) return fromHost

  const ua = input.userAgent || ''
  return NETWORK_BY_USER_AGENT.find(([pattern]) => pattern.test(ua))?.[1]
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>

const CLICK_ID_PARAMS: Record<string, string> = {
  gclid: 'google_ads',
  gbraid: 'google_ads',
  wbraid: 'google_ads',
  fbclid: 'meta',
  igshid: 'instagram',
  ttclid: 'tiktok',
  msclkid: 'microsoft_ads',
  li_fat_id: 'linkedin',
  twclid: 'x'
}

const SEARCH_HOSTS = /(^|\.)(google\.[a-z.]+|bing\.com|duckduckgo\.com|yahoo\.[a-z.]+|ecosia\.org|qwant\.com|yandex\.[a-z.]+|baidu\.com|naver\.com|brave\.com|startpage\.com)$/
const SOCIAL_HOSTS = /(^|\.)(instagram\.com|facebook\.com|fb\.com|tiktok\.com|pinterest\.[a-z.]+|pin\.it|x\.com|twitter\.com|t\.co|linkedin\.com|lnkd\.in|threads\.net|reddit\.com|youtube\.com|snapchat\.com|whatsapp\.com|telegram\.org)$/
const AI_HOSTS = /(^|\.)(chatgpt\.com|openai\.com|perplexity\.ai|claude\.ai|gemini\.google\.com|copilot\.microsoft\.com|you\.com|phind\.com|mistral\.ai|chat\.mistral\.ai|meta\.ai|grok\.com)$/
const EMAIL_HOSTS = /(^|\.)(mail\.google\.com|outlook\.(live|office)\.com|mail\.yahoo\.com|mail\.proton\.me|klaviyo\.com|klclick\d*\.com)$/

const clip = (value: unknown, max: number) => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed ? trimmed.slice(0, max) : undefined
}

const sanitizePath = (value: unknown) => {
  const raw = clip(value, 2048)
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return undefined
  return raw.split(/[?#]/)[0].slice(0, MAX_PATH_LENGTH) || '/'
}

const sanitizeReferrer = (value: unknown) => {
  const raw = clip(value, 2048)
  if (!raw) return undefined

  try {
    const url = new URL(raw)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return undefined
    const path = url.pathname === '/' ? '' : url.pathname
    return `${url.hostname}${path}`.slice(0, MAX_PATH_LENGTH)
  } catch {
    // Already stored as host/path.
    return /^[a-z0-9.-]+\.[a-z]{2,}(\/[^\s?#]*)?$/i.test(raw) ? raw.slice(0, MAX_PATH_LENGTH) : undefined
  }
}

const sanitizeUtm = (value: unknown): VisitorJourneyUtm | undefined => {
  if (!value || typeof value !== 'object') return undefined
  const input = value as Record<string, unknown>
  const utm: VisitorJourneyUtm = {
    source: clip(input.source, MAX_UTM_LENGTH),
    medium: clip(input.medium, MAX_UTM_LENGTH),
    campaign: clip(input.campaign, MAX_UTM_LENGTH),
    term: clip(input.term, MAX_UTM_LENGTH),
    content: clip(input.content, MAX_UTM_LENGTH)
  }
  const compact = Object.fromEntries(Object.entries(utm).filter(([, v]) => v)) as VisitorJourneyUtm
  return Object.keys(compact).length ? compact : undefined
}

const toTimestamp = (value: unknown) => {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) && n > 0 ? Math.round(n) : undefined
}

export const getReferrerHost = (referrer?: string) => referrer?.split('/')[0]?.replace(/^www\./, '') || undefined

export function classifyTrafficChannel(journey: Pick<VisitorJourney, 'clickIds' | 'referrer' | 'utm'>): TrafficChannel {
  const medium = journey.utm?.medium?.toLowerCase() || ''
  const source = journey.utm?.source?.toLowerCase() || ''
  const host = getReferrerHost(journey.referrer) || ''
  const clickIds = journey.clickIds || []
  // Instagram / Facebook add a click id (fbclid) to EVERY outbound link, ads or not.
  // An explicit UTM medium (e.g. "social" on the link in bio) therefore wins over it.
  const onlyMetaClickIds = clickIds.length > 0 && clickIds.every(id => id === 'meta' || id === 'instagram')
  const metaClickIdMeansPaid = !(medium && onlyMetaClickIds)
  const isPaid = /(cpc|ppc|paid|ads?$|display|cpm|sponsored)/.test(medium) ||
    clickIds.some(id => id.endsWith('_ads') || (id === 'meta' && metaClickIdMeansPaid) || id === 'tiktok' || id === 'linkedin' || id === 'x')
  const isSocial = SOCIAL_HOSTS.test(host) ||
    /(social|instagram|facebook|tiktok|pinterest|linkedin|twitter|^x$|^ig$|^fb$)/.test(`${medium} ${source}`) ||
    clickIds.some(id => ['meta', 'instagram', 'tiktok', 'linkedin', 'x'].includes(id))

  if (medium === 'email' || /(newsletter|klaviyo|email)/.test(source) || EMAIL_HOSTS.test(host)) return 'email'
  if (isPaid && isSocial) return 'paid_social'
  if (isPaid) return 'paid_search'
  if (AI_HOSTS.test(host) || /(chatgpt|openai|perplexity|claude|gemini|copilot)/.test(source)) return 'ai_assistant'
  if (isSocial) return 'organic_social'
  if (SEARCH_HOSTS.test(host) || medium === 'organic') return 'organic_search'
  if (host || source) return 'referral'
  return 'direct'
}

export const TRAFFIC_CHANNEL_LABELS: Record<TrafficChannel, string> = {
  ai_assistant: 'Assistant IA',
  direct: 'Direct',
  email: 'Email',
  organic_search: 'Recherche organique',
  organic_social: 'Réseau social (organique)',
  paid_search: 'Publicité (search/display)',
  paid_social: 'Publicité sociale',
  referral: 'Site référent'
}

const CONSENT_STATUSES = new Set(['accepted', 'partial', 'refused', 'pending', 'unknown'])

const normalizeConsent = (value: unknown): VisitorJourneyConsent | undefined => {
  if (!value || typeof value !== 'object') return undefined
  const input = value as Record<string, unknown>
  const status = typeof input.status === 'string' && CONSENT_STATUSES.has(input.status)
    ? input.status as VisitorJourneyConsent['status']
    : 'unknown'
  const visitorId = typeof input.visitorId === 'string' && /^[a-z0-9-]{8,64}$/i.test(input.visitorId) ? input.visitorId : undefined
  const decidedAt = toTimestamp(input.decidedAt)
  return {
    status,
    analytics: typeof input.analytics === 'boolean' ? input.analytics : undefined,
    marketing: typeof input.marketing === 'boolean' ? input.marketing : undefined,
    visitorId,
    decidedAt
  }
}

const createJourneyId = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  } catch {
    // Fall through.
  }
  return `j-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Snapshot of the CMP choice (CMPKit first, app consent state as fallback).
 */
export function captureConsentSnapshot(fallback?: { analytics?: boolean, marketing?: boolean } | null): VisitorJourneyConsent {
  let analytics: boolean | undefined
  let marketing: boolean | undefined
  let decided: boolean | undefined
  let visitorId: string | undefined
  let decidedAt: number | undefined

  try {
    const cmpkit = typeof window !== 'undefined' ? (window as any).cmpkit : null
    const consent = cmpkit?.getConsent?.()
    if (consent && typeof consent === 'object') {
      if (typeof consent.analytics === 'boolean') analytics = consent.analytics
      if (typeof consent.marketing === 'boolean') marketing = consent.marketing
    }
    // CMPKit reports whether a choice is stored; without it, defaults
    // (all false) would look like a refusal.
    const stored = cmpkit?.getRuntimeDebug?.()?.settings?.hasStoredConsent
    if (typeof stored === 'boolean') decided = stored
  } catch {
    // CMP unavailable.
  }

  try {
    visitorId = window.localStorage.getItem('cmpkit_visitor_id') || undefined
    const key = Object.keys(window.localStorage).find(name => name.startsWith('cmpkit_consent_'))
    const record = key ? JSON.parse(window.localStorage.getItem(key) || 'null') : null
    if (typeof record?.updatedAt === 'number') decidedAt = record.updatedAt
    if (decided === undefined && record?.consent) decided = true
  } catch {
    // Storage blocked.
  }

  if (analytics === undefined && typeof fallback?.analytics === 'boolean') analytics = fallback.analytics
  if (marketing === undefined && typeof fallback?.marketing === 'boolean') marketing = fallback.marketing

  let status: VisitorJourneyConsent['status'] = 'unknown'
  if (decided === false) status = 'pending'
  else if (analytics && marketing) status = 'accepted'
  else if (analytics || marketing) status = 'partial'
  else if (analytics === false && marketing === false) status = decided ? 'refused' : 'pending'

  return { status, analytics, marketing, visitorId, decidedAt }
}

/**
 * Validates an untrusted journey payload (client body or storage).
 */
export function normalizeVisitorJourney(value: unknown): VisitorJourney | null {
  if (!value || typeof value !== 'object') return null
  const input = value as Record<string, unknown>
  const startedAt = toTimestamp(input.startedAt)
  const landingPath = sanitizePath(input.landingPath)
  if (!startedAt || !landingPath) return null

  const pages = Array.isArray(input.pages)
    ? input.pages
        .map((page) => {
          const entry = page as Record<string, unknown> | null
          const path = sanitizePath(entry?.path)
          const at = toTimestamp(entry?.at)
          return path && at ? { path, at } : null
        })
        .filter((page): page is VisitorJourneyPage => page !== null)
        .slice(-VISITOR_JOURNEY_MAX_PAGES)
    : []

  const clickIds = Array.isArray(input.clickIds)
    ? [...new Set(input.clickIds.filter((id): id is string => typeof id === 'string' && Object.values(CLICK_ID_PARAMS).includes(id)))]
    : []

  const id = typeof input.id === 'string' && /^[a-z0-9-]{8,64}$/i.test(input.id) ? input.id : undefined

  return {
    id,
    consent: normalizeConsent(input.consent),
    startedAt,
    landingPath,
    referrer: sanitizeReferrer(input.referrer),
    utm: sanitizeUtm(input.utm),
    clickIds: clickIds.length ? clickIds : undefined,
    pages
  }
}

export function snapshotVisitorJourney(
  journey: VisitorJourney,
  now = Date.now(),
  userAgent?: string | null
): VisitorJourneySnapshot {
  const lastAt = journey.pages.at(-1)?.at || journey.startedAt
  const socialNetwork = detectSocialNetwork({ referrer: journey.referrer, utm: journey.utm, userAgent })
  let channel = classifyTrafficChannel(journey)
  // An in-app browser (Instagram, Pinterest…) without referrer is social, not direct.
  if (socialNetwork && (channel === 'direct' || channel === 'referral')) channel = 'organic_social'
  return {
    ...journey,
    channel,
    socialNetwork,
    durationSeconds: Math.max(0, Math.round((Math.max(now, lastAt) - journey.startedAt) / 1000)),
    pageCount: journey.pages.length,
    referrerHost: getReferrerHost(journey.referrer)
  }
}

const readStored = (storage: StorageLike): VisitorJourney | null => {
  try {
    const raw = storage.getItem(VISITOR_JOURNEY_STORAGE_KEY)
    return raw ? normalizeVisitorJourney(JSON.parse(raw)) : null
  } catch {
    return null
  }
}

/**
 * Appends the current page to the session journey, creating it on first call.
 * `currentUrl` must be absolute; `documentReferrer` is ignored when internal.
 */
export function recordVisitorJourneyPage(
  storage: StorageLike,
  currentUrl: string,
  documentReferrer = '',
  now = Date.now()
): VisitorJourney | null {
  let url: URL
  try {
    url = new URL(currentUrl)
  } catch {
    return null
  }

  const path = sanitizePath(url.pathname)
  if (!path) return null

  let journey = readStored(storage)

  if (!journey) {
    let referrer: string | undefined
    try {
      if (documentReferrer && new URL(documentReferrer).hostname !== url.hostname) {
        referrer = sanitizeReferrer(documentReferrer)
      }
    } catch {
      referrer = undefined
    }

    const params = url.searchParams
    const clickIds = [...new Set(
      Object.entries(CLICK_ID_PARAMS)
        .filter(([param]) => params.has(param))
        .map(([, network]) => network)
    )]

    journey = {
      id: createJourneyId(),
      startedAt: now,
      landingPath: path,
      referrer,
      utm: sanitizeUtm({
        source: params.get('utm_source'),
        medium: params.get('utm_medium'),
        campaign: params.get('utm_campaign'),
        term: params.get('utm_term'),
        content: params.get('utm_content')
      }),
      clickIds: clickIds.length ? clickIds : undefined,
      pages: []
    }
  }

  if (!journey.id) journey.id = createJourneyId()

  if (journey.pages.at(-1)?.path !== path) {
    journey.pages = [...journey.pages, { path, at: now }].slice(-VISITOR_JOURNEY_MAX_PAGES)
  }

  try {
    storage.setItem(VISITOR_JOURNEY_STORAGE_KEY, JSON.stringify(journey))
  } catch {
    // Storage full or blocked: journey stays best-effort.
  }

  return journey
}

export function readVisitorJourney(storage?: StorageLike | null): VisitorJourney | null {
  if (!storage) return null
  return readStored(storage)
}

export function getBrowserSessionStorage(): StorageLike | null {
  if (typeof window === 'undefined') return null
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

export function getCurrentJourneyId(storage?: StorageLike | null): string | undefined {
  return readVisitorJourney(storage ?? null)?.id
}

/**
 * Journey + CMP snapshot sent with lead forms (undefined outside the browser).
 */
export function buildJourneySubmission(): VisitorJourney | undefined {
  const journey = readVisitorJourney(getBrowserSessionStorage())
  return journey ? { ...journey, consent: captureConsentSnapshot() } : undefined
}
